import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { confirmSignIn } from 'aws-amplify/auth';

import { LoadingDots } from '@reckon-web/loading';
import { Flex } from '@reckon-web/flex';

import { getCurrentCognitoUser } from '../utils/helper';
import { NewPasswordForm, VerifyMFAForm } from '../components/authentication';
import { useAuthenticatedUser, useClient } from '../components/hooks';
import { SignInComponent } from '../components/authentication/signIn';
import { nextAction } from '../components/authentication/useAuthentication';
import { useChallengeState } from '../components/authentication/useAuthentication/useChallengeUser';
import { Layout } from '../components/layout/layout';
import { useNavigationWithQuery } from '../components/authentication/useNavigationWithQuery';
import { useRedirectToClient } from '../components/authentication/useRedirectToClient';

export default function Page() {
  const router = useRouter();

  const {
    clientId,
    redirectURL,
    tenancy: TENANCY,
    product,
    mfaToken,
  } = useClient();

  const [pageLoading, setPageLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { challengeState, setChallengeState } = useChallengeState();
  const { authenticationStatus, authenticatedUser } = useAuthenticatedUser({
    bypassCache: true,
  });
  const { navigateTo } = useNavigationWithQuery();
  const { redirectToClient, redirectingToClient } = useRedirectToClient();

  const [resetPasswordSuccess, setResetPasswordSuccess] =
    useState<boolean>(false);

  useEffect(() => {
    if (authenticatedUser) {
      navigateTo({ path: '' });
    } else if (authenticationStatus === 'unauthenticated') {
      setPageLoading(false);
    }
  }, [authenticatedUser, navigateTo, authenticationStatus]);

  useEffect(() => {
    setResetPasswordSuccess(router?.query?.redirectedFrom === 'reset-password');
  }, [router]);

  const resetPassword = async (value: any) => {
    if (!value.password) return;
    try {
      const resp = await confirmSignIn({
        challengeResponse: value.password,
      });

      if (resp.isSignedIn) {
        // Get user data for successful sign-in
        const user = await getCurrentCognitoUser();

        await nextAction({
          user: user,
          router: router,
          redirectURL,
          TENANCY,
          clientId,
          mfaToken,
        });
      } else if (resp.nextStep) {
        if (
          resp.nextStep.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          return;
        }
      } else {
        setChallengeState(null);
      }
    } catch (err: any) {
      setErrorMessage(err.message);
      if (err.message.includes('session is expired')) {
        router.push({ pathname: '/login', query: router.query });
      }
    }
  };

  const confirmMFA = async (value: any) => {
    if (!value.code) return;

    try {
      const resp = await confirmSignIn({
        challengeResponse: value.code,
        options: {
          MFAValidation: '1',
        },
      });

      if (resp.isSignedIn) {
        // Get user data for successful sign-in
        const user = await getCurrentCognitoUser();

        await nextAction({
          user: user,
          router: router,
          redirectURL,
          TENANCY,
          clientId,
          mfaToken,
        });
      } else if (
        resp.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
      ) {
        return;
      }
    } catch (err: any) {
      setErrorMessage(err.message);
      if (err.message.includes('session is expired')) {
        router.push({ pathname: '/login', query: router.query });
      }
    }
  };

  if (!clientId || pageLoading || redirectingToClient) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <LoadingDots label="Initial data loading" size="large" />
      </Flex>
    );
  }

  return (
    <Layout theme={TENANCY} product={product}>
      {challengeState === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ? (
        <VerifyMFAForm
          onSubmit={confirmMFA}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      ) : challengeState === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' ? (
        <NewPasswordForm
          onSubmit={resetPassword}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      ) : (
        <SignInComponent
          resetPasswordSuccess={resetPasswordSuccess}
          resetResetPasswordSuccess={() => setResetPasswordSuccess(false)}
          onRedirectToClient={() => redirectToClient()}
          navigateToResetPassword={() => navigateTo({ path: 'reset-password' })}
          navigateToSignup={() => navigateTo({ path: 'signup' })}
          navigateToForgotPassword={() =>
            navigateTo({ path: 'forgot-password' })
          }
          navigateToVerifyMfa={() => navigateTo({ path: 'verifycode' })}
          navigateToSetupMfa={() => navigateTo({ path: 'mfa' })}
        />
      )}
    </Layout>
  );
}
